/*
 * (c) Copyright, 2019, Medidata Solutions Worldwide, Inc. All rights reserved.
 */
import _ from 'lodash'
import moment from 'moment'
import React from 'react'

export function swapArrayElements<T>(array: T[], index1: number, index2: number): T[] {
  if (!Array.isArray(array)) {
    return []
  }
  if (index1 < 0 || index2 < 0 || index1 >= array.length || index2 >= array.length) {
    return array
  }
  const newArray = [...array]
  ;[newArray[index1], newArray[index2]] = [newArray[index2], newArray[index1]]
  return newArray
}

class Utils {
  static getFormDefaults(
    vals: object,
    validationSchema: {
      cast: (vals: object, opts: { recursive: boolean; stripUnknown: boolean; context: object }) => object
    }
  ): object {
    const defaults = validationSchema.cast(vals, {
      recursive: true,
      stripUnknown: true,
      context: vals
    })
    return defaults
  }
  static readonly indexOfCaseInsenstive = function (a: string, b: string): number {
    a = a.toLowerCase()
    b = b.toLowerCase()

    return a.indexOf(b)
  }

  // Use when input = selectProps.inputValue and option=label while using Select with components={{ Option }}
  // Refer ArtifactSelector.jsx
  static readonly highlight_search = function (
    input: string,
    option: string,
    isOptionPredicted = false
  ): React.ReactElement {
    const num = Utils.indexOfCaseInsenstive(option, input)
    const len = input.length

    return (
      <div className={isOptionPredicted ? 'pl5 pt1 mt3 text-predicted' : 'pl5 pt1 mt3'}>
        <span>{option.substring(0, num >= 0 ? num : 0)}</span>
        {num >= 0 ? (
          <span>
            <b>{option.substring(num, num + len)}</b>
          </span>
        ) : null}
        <span>{option.substring(num >= 0 ? num + len : 0)}</span>
      </div>
    )
  }

  static formatTextAreaCounter(length: number, max: number, text: string): React.ReactElement {
    const diff = max - length
    return (
      <span>
        {text}: <span className={diff < 0 ? 'text-danger' : ''}>{max - length}</span>
      </span>
    )
  }

  static convertDateForServer(val: any, endOfDay?: boolean): any {
    if (val !== null && val && typeof val === 'object' && 'utc' in val && typeof val.utc === 'function') {
      const utcDateAt = val.utc(true)
      utcDateAt.set(
        endOfDay
          ? { hour: 23, minute: 59, second: 59, millisecond: 0 }
          : { hour: 0, minute: 0, second: 0, millisecond: 0 }
      )
      return utcDateAt.valueOf()
    }
    return val
  }

  static transformToSelectOptions(
    arr: any[],
    valueKey: string = 'id',
    labelKey: string = 'value',
    sortBy: string | null = null,
    preserveOrigVals: boolean = false
  ): Array<{ value: any; label: string; [key: string]: any }> {
    if (!Array.isArray(arr)) {
      return []
    }

    let sortedArr = arr
    if (sortBy && sortBy.length) {
      sortedArr = [...arr].sort((a, b) => {
        const aVal = a[sortBy]
        const bVal = b[sortBy]
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          return aVal.toLowerCase().localeCompare(bVal.toLowerCase())
        }
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0
      })
    }

    return sortedArr.map((val: any) => {
      if (typeof val === 'string') {
        return {
          value: val,
          label: val
        }
      }

      return {
        ...(preserveOrigVals ? val : {}),
        value: val[valueKey],
        label: val[labelKey]
      }
    })
  }

  /**
   * Converts a timestamp to a local moment preserving the UTC date.
   * Extracts UTC year/month/date and creates a LOCAL moment so that
   * .toDate() followed by local-time formatting always shows the
   * correct calendar date (matches legacy lego/lib/Utils.jsx).
   * @param value - timestamp or date string
   * @returns moment.Moment (local, midnight on the UTC calendar date)
   */
  static convertTimestampToUTCMoment(value: number | string | Date): moment.Moment {
    const utcMoment = moment.utc(value)
    // Build a local moment from UTC date parts to avoid timezone day-shift
    return moment({
      year: utcMoment.year(),
      month: utcMoment.month(),
      date: utcMoment.date()
    })
  }
}

export default Utils
